/**
 * IntegrationConfigDialog Component
 * 
 * Dialog for managing company product integrations
 * Supports multiple product types with instance-based configuration
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Plus, RefreshCw } from 'lucide-react';
import type { Company, ProductInstance } from '@/features/multi-company/types/company.types';
import { AddProductDialog } from './AddProductDialog';
import { ProductInstancesList } from './ProductInstancesList';
import type { AddProductRequest, UpdateProductRequest } from '@/types/product';

interface SyncResult {
  productInstanceId: string;
  status: 'success' | 'partial' | 'failed';
  recordsImported: number;
  recordsUpdated: number;
  recordsFailed: number;
  startedAt: string;
  completedAt: string;
  errors?: string[];
  warnings?: string[];
}

interface IntegrationConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: Company | null;
  onSave: (company: Company) => void;
  // API callbacks for Live mode
  onAddProduct?: (companyId: string, request: AddProductRequest) => Promise<ProductInstance[]>;
  onUpdateProduct?: (companyId: string, instanceId: string, request: UpdateProductRequest) => Promise<ProductInstance>;
  onDeleteProduct?: (companyId: string, instanceId: string) => Promise<void>;
  onSyncProduct?: (companyId: string, instanceId: string) => Promise<SyncResult>;
  onRefresh?: (companyId: string) => Promise<ProductInstance[]>;
  isLiveMode?: boolean;
}

export function IntegrationConfigDialog({
  open,
  onOpenChange,
  company,
  onSave,
  onAddProduct,
  onUpdateProduct,
  onDeleteProduct,
  onSyncProduct,
  onRefresh,
  isLiveMode = false,
}: IntegrationConfigDialogProps) {
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Update editingCompany when company prop changes
  useEffect(() => {
    if (company) {
      setEditingCompany(company);
    }
  }, [company]);

  const handleAddProduct = async (newInstances: ProductInstance[]) => {
    if (!editingCompany) return;

    if (isLiveMode && onAddProduct && newInstances.length > 0) {
      // In Live mode, call the API to create the product
      setIsLoading(true);
      try {
        const mainInstance = newInstances[0];
        const request: AddProductRequest = {
          productType: mainInstance.productType,
          instanceName: mainInstance.instanceName,
          syncFrequency: mainInstance.syncFrequency,
          autoSync: mainInstance.autoSync,
          externalInstanceId: mainInstance.externalInstanceId || undefined,
        };
        const createdInstances = await onAddProduct(editingCompany.id, request);
        setEditingCompany({
          ...editingCompany,
          productInstances: [
            ...editingCompany.productInstances,
            ...createdInstances,
          ],
        });
      } catch (error) {
        console.error('Failed to add product:', error);
        // Error is handled by the parent component
      } finally {
        setIsLoading(false);
      }
    } else {
      // In Demo mode, just update local state
      setEditingCompany({
        ...editingCompany,
        productInstances: [
          ...editingCompany.productInstances,
          ...newInstances,
        ],
      });
    }
  };

  const handleUpdateProduct = async (updatedInstance: ProductInstance) => {
    if (!editingCompany) return;

    if (isLiveMode && onUpdateProduct) {
      // In Live mode, call the API to update the product
      setIsLoading(true);
      try {
        const request: UpdateProductRequest = {
          instanceName: updatedInstance.instanceName,
          syncFrequency: updatedInstance.syncFrequency,
          autoSync: updatedInstance.autoSync,
        };
        const updated = await onUpdateProduct(editingCompany.id, updatedInstance.id, request);
        setEditingCompany({
          ...editingCompany,
          productInstances: editingCompany.productInstances.map((instance) =>
            instance.id === updated.id ? updated : instance
          ),
        });
      } catch (error) {
        console.error('Failed to update product:', error);
      } finally {
        setIsLoading(false);
      }
    } else {
      // In Demo mode, just update local state
      setEditingCompany({
        ...editingCompany,
        productInstances: editingCompany.productInstances.map((instance) =>
          instance.id === updatedInstance.id ? updatedInstance : instance
        ),
      });
    }
  };

  const handleDeleteProduct = async (instanceId: string) => {
    if (!editingCompany) return;

    if (isLiveMode && onDeleteProduct) {
      // In Live mode, call the API to delete the product
      setIsLoading(true);
      try {
        await onDeleteProduct(editingCompany.id, instanceId);
        setEditingCompany({
          ...editingCompany,
          productInstances: editingCompany.productInstances.filter(
            (instance) => instance.id !== instanceId && instance.autoConnectedByInstanceId !== instanceId
          ),
        });
      } catch (error) {
        console.error('Failed to delete product:', error);
      } finally {
        setIsLoading(false);
      }
    } else {
      // In Demo mode, just update local state
      setEditingCompany({
        ...editingCompany,
        productInstances: editingCompany.productInstances.filter(
          (instance) => instance.id !== instanceId && instance.autoConnectedByInstanceId !== instanceId
        ),
      });
    }
  };

  const handleRefresh = async () => {
    if (!editingCompany || !onRefresh) return;
    
    setIsLoading(true);
    try {
      const instances = await onRefresh(editingCompany.id);
      setEditingCompany({
        ...editingCompany,
        productInstances: instances,
      });
    } catch (error) {
      console.error('Failed to refresh products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSyncProduct = async (instanceId: string): Promise<SyncResult> => {
    if (!editingCompany || !onSyncProduct) {
      throw new Error('Sync not available');
    }
    return onSyncProduct(editingCompany.id, instanceId);
  };

  const handleSave = () => {
    if (editingCompany) {
      onSave(editingCompany);
    }
    onOpenChange(false);
  };

  if (!company || !editingCompany) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Integration Configuration</DialogTitle>
            <DialogDescription>
              Configure product integrations for {company.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-medium">Configured Products</h3>
              <div className="flex gap-2">
                {isLiveMode && onRefresh && (
                  <Button variant="outline" onClick={handleRefresh} disabled={isLoading}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                )}
                <Button onClick={() => setShowAddProduct(true)} disabled={isLoading}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Product
                </Button>
              </div>
            </div>

            <ProductInstancesList
              instances={editingCompany.productInstances}
              onUpdate={handleUpdateProduct}
              onDelete={handleDeleteProduct}
              onSync={isLiveMode && onSyncProduct ? handleSyncProduct : undefined}
            />

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave}>
                Save Configuration
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AddProductDialog
        open={showAddProduct}
        onOpenChange={setShowAddProduct}
        companyId={company.id}
        companyCode={company.code}
        existingInstances={editingCompany.productInstances}
        onAdd={handleAddProduct}
      />
    </>
  );
}
